import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CHAMPS_FICHE, CLE_CODE_POSTAL_ECOLE } from '@repere/core';
import { Alerte, Bouton, Invisible } from '../ui';
import { messageDErreur, useSession } from '../auth/session';
import { cleSourceConges } from '../compte/SourceConges';
import { CorrigerEnfant } from './CorrigerEnfant';
import type { EnfantDuDossier } from './types';
import { GROUPES, PRESENTATION, champsDuGroupe } from './champsFiche';
import { ChoixAvatar } from './ChoixAvatar';
import { dateComplete } from './dates';
import { SaisieChamp } from './SaisiesFiche';

interface ChampFiche {
  key: string;
  libelle: string;
  sensible: boolean;
  value: string | null;
  validFrom: string | null;
  updatedById: string | null;
}

interface VersionChamp {
  id: string;
  key: string;
  value: string;
  validFrom: string;
  supersededAt: string | null;
  updatedById: string;
}

/**
 * Fiche enfant — informations pratiques partagées (docs/02 §6).
 *
 * Les champs viennent de `@repere/core` : la même liste fermée que le serveur, pour que
 * l'interface ne puisse pas proposer ce que l'API refuserait. Aucun champ libre n'y
 * figure, et c'est la mesure de minimisation elle-même.
 *
 * Les champs de santé sont signalés comme chiffrés. Le dire est utile : un parent qui
 * hésite à renseigner une allergie doit savoir ce qu'il en advient.
 *
 * **La saisie est directe** : chaque champ est sa propre commande, groupée par thème, sans
 * bouton « Modifier » à franchir. Les dix champs partageaient auparavant la même zone de
 * texte libre, toutes ouvertes en permanence avec chacune son bouton d'enregistrement —
 * dix fois le même geste pour dix questions qui n'ont rien de commun, et un « O+ » qui
 * pouvait s'écrire « 0+ ».
 *
 * Cette disposition a deux écueils, et ils sont traités ici plutôt qu'admis :
 *
 * - **La date de dernière modification** n'a plus de bouton où se loger : elle se range à
 *   droite de l'intitulé. Sans elle, on ne saurait plus depuis quand une allergie est
 *   enregistrée — la question que la fiche existe pour répondre.
 * - **L'enregistrement groupé** ne dirait plus ce qu'il inscrit dans un historique
 *   inaltérable. La barre du bas ne paraît donc qu'à la première modification, et **nomme
 *   les champs** qu'elle s'apprête à dater.
 */
export function FicheEnfant({
  caseFileId,
  enfant,
  maintenant,
}: {
  caseFileId: string;
  enfant: EnfantDuDossier;
  maintenant: Date;
}) {
  const childId = enfant.id;
  const prenom = enfant.firstName;
  const { client } = useSession();
  /**
   * L'onglet ouvert.
   *
   * « Identité » d'abord : c'est ce qu'on vient vérifier le plus souvent, et c'est aussi
   * la partie qui tient en un écran. Les détails sont une consultation, pas un accueil.
   */
  const [onglet, setOnglet] = useState<'identite' | 'details'>('identite');
  const queryClient = useQueryClient();
  /**
   * Les valeurs en cours de saisie, par clé.
   *
   * Seules les clés que le parent a touchées y figurent : une clé absente signifie « rien
   * n'a bougé », ce qui rend le calcul des modifications exact sans avoir à comparer la
   * fiche entière à chaque frappe.
   */
  const [brouillon, setBrouillon] = useState<Record<string, string>>({});
  const [historiqueOuvert, setHistoriqueOuvert] = useState<string | null>(null);

  const fiche = useQuery({
    queryKey: ['fiche', childId],
    queryFn: () => client.request<ChampFiche[]>(`/children/${childId}/profile`),
  });

  const historique = useQuery({
    queryKey: ['fiche-historique', childId, historiqueOuvert],
    queryFn: () =>
      client.request<VersionChamp[]>(`/children/${childId}/profile/${historiqueOuvert}/history`),
    enabled: historiqueOuvert !== null,
  });

  /**
   * Enregistre les champs modifiés, un par un.
   *
   * En série et non en parallèle : chaque écriture s'insère dans une chaîne d'empreintes
   * côté serveur, et deux requêtes concurrentes sur le même dossier se disputeraient le
   * même maillon. L'attente est de quelques dizaines de millisecondes par champ, pour un
   * geste qu'on pose rarement.
   *
   * Un échec en cours de route laisse enregistré ce qui l'a été — c'est le comportement
   * honnête : les champs passés sont réellement datés, et le brouillon n'est vidé qu'au
   * succès complet, de sorte que ce qui reste à l'écran est ce qui reste à écrire.
   */
  const enregistrer = useMutation({
    mutationFn: async (modifications: { key: string; value: string }[]) => {
      for (const modification of modifications) {
        await client.request(`/children/${childId}/profile`, {
          method: 'PUT',
          body: modification,
        });
      }
    },
    onSuccess: (_resultat, modifications) => {
      setBrouillon({});
      void queryClient.invalidateQueries({ queryKey: ['fiche', childId] });
      void queryClient.invalidateQueries({ queryKey: ['fiche-historique', childId] });

      // Le code postal de l'école décide la zone de vacances, et la zone décide ce que le
      // calendrier encadre. La source des congés est gardée trente minutes — délai voulu,
      // elle ne bouge qu'à un déménagement — mais ce geste-ci **est** ce déménagement :
      // sans cette ligne, le parent renseigne l'école, ouvre le calendrier, et n'y voit
      // aucune vacance jusqu'à ce qu'il recharge la page.
      if (modifications.some((modification) => modification.key === CLE_CODE_POSTAL_ECOLE)) {
        void queryClient.invalidateQueries({ queryKey: cleSourceConges(caseFileId) });
      }
    },
  });

  const valeurActuelle = (key: string): ChampFiche | undefined =>
    fiche.data?.find((champ) => champ.key === key);

  const valeurEnCours = (key: string): string =>
    brouillon[key] ?? valeurActuelle(key)?.value ?? '';

  /**
   * Les champs réellement modifiés.
   *
   * Comparés à la valeur enregistrée, jamais à « le parent a-t-il touché ce champ » : taper
   * une lettre puis l'effacer ne doit pas laisser une modification fantôme dans la barre,
   * ni dater un champ pour rien dans un historique inaltérable.
   */
  const modifies = Object.keys(brouillon).filter(
    (cle) => (brouillon[cle] ?? '') !== (valeurActuelle(cle)?.value ?? ''),
  );

  /**
   * Combien de champs modifiés dans ce bloc.
   *
   * Un bloc replié qui contient une modification en attente doit le dire : sinon replier
   * suffirait à faire disparaître de l'écran ce qu'on s'apprête à inscrire dans un
   * historique inaltérable.
   */
  const modifiesDuGroupe = (groupe: string) =>
    modifies.filter((cle) => PRESENTATION[cle]?.groupe === groupe).length;

  return (
    <>
      {/* Les onglets reprennent le dessin des filtres de la messagerie et du journal :
          c'est le même geste — choisir ce qu'on regarde dans un même écran —, et lui
          donner une deuxième apparence n'aurait rien appris à personne. */}
      <div className="filtres" role="tablist" aria-label={`Fiche de ${prenom}`}>
        <button
          type="button"
          role="tab"
          id="onglet-identite"
          aria-selected={onglet === 'identite'}
          aria-controls="volet-identite"
          className={onglet === 'identite' ? 'filtre-onglet actif' : 'filtre-onglet'}
          onClick={() => setOnglet('identite')}
        >
          Identité
        </button>
        <button
          type="button"
          role="tab"
          id="onglet-details"
          aria-selected={onglet === 'details'}
          aria-controls="volet-details"
          className={onglet === 'details' ? 'filtre-onglet actif' : 'filtre-onglet'}
          onClick={() => setOnglet('details')}
        >
          Détails
          {/* Une modification en attente dans l'autre onglet doit se voir depuis celui-ci :
              la barre d'enregistrement dit *quoi*, cette pastille dit *où*. */}
          {modifies.length > 0 ? (
            <span className="filtre-compte" aria-hidden="true">
              {modifies.length}
            </span>
          ) : null}
          {modifies.length > 0 ? (
            <Invisible>, {modifies.length} champ(s) modifié(s) non enregistré(s)</Invisible>
          ) : null}
        </button>
      </div>

      {fiche.isError ? (
        <Alerte titre="La fiche n’a pas pu être chargée">{messageDErreur(fiche.error)}</Alerte>
      ) : null}

      {enregistrer.isError ? (
        <Alerte titre="La modification n’a pas pu être enregistrée">
          {messageDErreur(enregistrer.error)}
        </Alerte>
      ) : null}

      {onglet === 'identite' ? (
        <div role="tabpanel" id="volet-identite" aria-labelledby="onglet-identite">
          <ChoixAvatar
            childId={childId}
            prenom={prenom}
            naissance={enfant.birthDate}
            avatar={enfant.avatar}
            maintenant={maintenant}
          />
          <CorrigerEnfant caseFileId={caseFileId} enfant={enfant} />
        </div>
      ) : (
        <div role="tabpanel" id="volet-details" aria-labelledby="onglet-details">
          <p className="discret" style={{ marginTop: 0 }}>
            Ce que les deux parents doivent savoir sur {prenom}. Chaque modification est
            conservée : on voit qui a renseigné quoi, et depuis quand.
          </p>

          <div className="fiche-enfant">
            {GROUPES.map((groupe) => {
              const enAttente = modifiesDuGroupe(groupe.cle);
              return (
                /* `<details>` plutôt qu'un bouton et un état : le navigateur donne le
                   clavier, l'annonce au lecteur d'écran et la recherche dans la page —
                   Ctrl+F déplie un bloc fermé pour y montrer ce qu'il contient. */
                <details key={groupe.cle} className="groupe-fiche" open>
                  <summary>
                    <span>{groupe.titre}</span>
                    {enAttente > 0 ? (
                      <span className="modifie">
                        {enAttente === 1 ? '1 modification' : `${enAttente} modifications`}
                      </span>
                    ) : null}
                  </summary>

                  <div className="groupe-champs">
                    {champsDuGroupe(groupe.cle).map((champ) => {
                      const actuel = valeurActuelle(champ.cle);
                      const modifie = modifies.includes(champ.cle);
                      const presentation = PRESENTATION[champ.cle];
                      if (!presentation) return null;

                      return (
                        <div
                          key={champ.cle}
                          /* Un champ modifié se signale sur lui-même. La barre du bas dit
                             *combien* et *quoi* ; le contour dit *lequel*, et sans lui il
                             fallait relire la liste de la barre pour retrouver ce qu'on
                             venait de taper. */
                          className={modifie ? 'champ-fiche modifie-en-attente' : 'champ-fiche'}
                        >
                          <div className="entete-champ">
                            <span className="intitule-champ">
                              {champ.libelle}
                              {champ.sensible ? (
                                <span className="marque-chiffre" title="Chiffré au repos">
                                  chiffré
                                </span>
                              ) : null}
                            </span>

                            {/* À droite de l'intitulé : ce que le bouton « Modifier »
                                portait avant de disparaître. Une fiche qui ne dit plus
                                depuis quand une allergie est enregistrée ne répond plus à
                                ce qu'on lui demande.

                                La date reste affichée pendant la modification : elle disait
                                « modifié, non enregistré » à sa place, et l'on perdait
                                justement le repère auquel on compare ce qu'on est en train
                                d'écrire. Ce que la modification a à dire se dit maintenant
                                sous la saisie, là où le regard se trouve déjà. */}
                            <span className="trace-champ">
                              {actuel?.validFrom ? (
                                <>
                                  Depuis le {dateComplete(new Date(actuel.validFrom))}
                                  <button
                                    type="button"
                                    className="lien-historique"
                                    onClick={() =>
                                      setHistoriqueOuvert((ouvert) =>
                                        ouvert === champ.cle ? null : champ.cle,
                                      )
                                    }
                                  >
                                    {historiqueOuvert === champ.cle ? 'Masquer' : 'Historique'}
                                  </button>
                                </>
                              ) : (
                                'Jamais renseigné'
                              )}
                            </span>
                          </div>

                          <SaisieChamp
                            saisie={presentation.saisie}
                            intitule={champ.libelle}
                            valeur={valeurEnCours(champ.cle)}
                            onChange={(valeur) =>
                              setBrouillon((courant) => ({ ...courant, [champ.cle]: valeur }))
                            }
                          />

                          {/* Sous la saisie, et non en tête de champ : c'est ici que se
                              trouve l'œil de qui vient de taper. La fiche s'annonce comme
                              une « saisie directe, sans bouton Modifier à franchir », et
                              cette promesse laisse croire que taper suffit — un parent a
                              rempli le même champ cinq fois avant de trouver le bouton, en
                              bas d'écran, hors du chemin du regard.

                              La phrase nomme le geste qui reste et où il se trouve. Elle
                              disparaît dès que le champ est enregistré : c'est un rappel,
                              pas un état permanent. */}
                          {modifie ? (
                            <p className="attente-enregistrement">
                              Modifié — rien n’est conservé tant que vous n’avez pas cliqué
                              sur « Enregistrer », en bas de l’écran.
                            </p>
                          ) : null}

                          {champ.aide ? <p className="aide">{champ.aide}</p> : null}

                          {historiqueOuvert === champ.cle && historique.data ? (
                            <ul className="historique-champ">
                              {historique.data.map((version) => (
                                <li key={version.id}>
                                  « {version.value} » — depuis le{' '}
                                  {dateComplete(new Date(version.validFrom))}
                                  {version.supersededAt
                                    ? `, remplacé le ${dateComplete(new Date(version.supersededAt))}`
                                    : ' (en vigueur)'}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      )}

      {/* La barre vit **hors des volets** : une modification faite dans les détails puis
          masquée par un passage sur l'identité resterait sinon invisible, et se perdrait
          au premier rechargement. Elle ne paraît qu'à la première modification, et nomme
          ce qu'elle va dater. */}
      {modifies.length > 0 ? (
        <div className="barre-enregistrement">
          <div>
            <strong>
              {modifies.length === 1
                ? '1 champ modifié'
                : `${modifies.length} champs modifiés`}
            </strong>
            <p className="discret">
              {modifies
                .map((cle) => CHAMPS_FICHE.find((champ) => champ.cle === cle)?.libelle ?? cle)
                .join(', ')}
            </p>
          </div>
          <div className="barre-actions">
            <Bouton variante="secondaire" onClick={() => setBrouillon({})}>
              Annuler
            </Bouton>
            <Bouton
              disabled={enregistrer.isPending}
              onClick={() =>
                enregistrer.mutate(
                  modifies.map((cle) => ({ key: cle, value: brouillon[cle] ?? '' })),
                )
              }
            >
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Bouton>
          </div>
        </div>
      ) : null}
    </>
  );
}
